import MojitoServices from 'mojito/services';
import MojitoCore from 'mojito/core';
import { useDispatch, useSelector } from 'react-redux';
import { isString, pick } from 'mojito/utils';
import MojitoPresentation from 'mojito/presentation';
import { useMemo } from 'react';

const { StringUtils } = MojitoCore.Base;
const { uiStyle } = MojitoCore.Presentation;
const { Image, DotNotification, Button, AbsolutePane, GridLayout, Text, FlexPane } =
    MojitoPresentation.Components;
const { GridPane } = GridLayout;
const { selectBetCount } = MojitoServices.Bets.openBetsSelectors;
const intentActions = MojitoCore.Intents.actions;

export default function QuickLinks(props) {
    const {
        appContext: { analyticsEmitter },
    } = props.mojitoTools;

    const betCount = useSelector(state => selectBetCount(state));
    const dispatch = useDispatch();

    const onLinkClick = (event, data) => {
        const { intent, label } = data;
        if (intent.type) {
            analyticsEmitter.emitAnalytics('quickLinkClicked', label);
            const parsedIntentData = isString(intent.data) ? JSON.parse(intent.data) : intent.data;
            dispatch(intentActions.publishIntent(intent.type, parsedIntentData));
        }
    };

    const notifications = useMemo(() => ({ hasOpenBets: betCount > 0 }), [betCount]);

    return <QuickLinksView {...props} notifications={notifications} onLinkClick={onLinkClick} />;
}

const QuickLinksView = props => {
    const {
        notifications,
        onLinkClick,
        mojitoTools: { config, style },
    } = props;

    // we need to render dummyContainer and include it to normal layout flow to fix scrolling issue
    // that will happen if this component will be placed inside scrollable area.
    // Due to the `position:fixed` the container will be moved out from normal flow layout,
    // that's why there will be no possible to scroll content to the end. Dummy container fixes this
    // preserving space at the bottom of the container.
    return (
        <>
            <div style={style.dummyContainer} />
            <AbsolutePane config={config.container}>
                <GridPane config={config.linksContainer}>
                    {config.links?.map(link => (
                        <Link
                            {...props}
                            key={link.label}
                            notifications={notifications}
                            link={link}
                            onClick={onLinkClick}
                        />
                    ))}
                </GridPane>
            </AbsolutePane>
        </>
    );
};

const Link = props => {
    const {
        link,
        onClick,
        notifications,
        mojitoTools: { config, stringResolver },
    } = props;
    const notificationActions = link.notification?.onActions;
    const hasNotification = notificationActions?.some(action => notifications[action] === true);
    const isDotNotificationVisible = config.isDotNotificationVisible && hasNotification;
    const intent = {
        type: link.intent && link.intent.type,
        data: link.intent && JSON.stringify(link.intent.data),
    };
    const data = { intent, label: StringUtils.deriveLabelNamePrefix(link.label) };
    const label = stringResolver.resolveString(link.label);
    const linkStyle = useLinkConfig(link, config);
    return (
        <Button
            config={linkStyle.linkButton}
            onClickData={data}
            onClick={onClick}
            hrefLink={link.hrefLink}
            class={StringUtils.makeCssClassName('ta', link.label)}
            key={label}
        >
            <AbsolutePane config={linkStyle.linkDecorationContainer} />
            <FlexPane config={linkStyle.linkContainer}>
                {isDotNotificationVisible ? (
                    <DotNotification
                        config={linkStyle.dotNotification}
                        isVisible={isDotNotificationVisible}
                    >
                        <Image src={link.icon} config={linkStyle.linkIcon} />
                    </DotNotification>
                ) : (
                    <Image src={link.icon} config={linkStyle.linkIcon} />
                )}
                <Text config={linkStyle.linkLabel}>{label}</Text>
            </FlexPane>
        </Button>
    );
};

const useLinkConfig = (link, config) => {
    return useMemo(() => {
        const mergeLinkStyle = prop =>
            link[prop] ? uiStyle.merge(config[prop], link[prop]) : config[prop];
        const linkContainer = mergeLinkStyle('linkContainer');
        const linkButton = mergeLinkStyle('linkButton');
        const linkLabel = mergeLinkStyle('linkLabel');
        const linkIcon = mergeLinkStyle('linkIcon');
        const dotNotification = mergeLinkStyle('dotNotification');
        const linkDecorationContainer = mergeLinkStyle('linkDecorationContainer');
        return {
            linkContainer,
            linkButton,
            linkLabel,
            linkIcon,
            dotNotification,
            linkDecorationContainer,
        };
    }, [config, link]);
};

QuickLinks.getStyle = config => {
    const { container } = config;
    return {
        dummyContainer: pick(container.style, 'width', 'height', 'left', 'right', 'top', 'bottom'),
    };
};
